/**
 * Unified API Client for SmartBeez Platform
 * Handles all backend communication with proper authentication
 */

import {
  Agent,
  Workflow,
  Knowledge,
  ChatMessage,
  ResolvedAgent,
  ResolvedAgentsResponse,
  AgentOverride,
  CustomAgentInput,
  // Control API types
  Rule,
  RuleCreate,
  RuleUpdate,
  RuleListResponse,
  RuleCreateResponse,
  RuleListQuery,
  Scenario,
  ScenarioCreate,
  ScenarioUpdate,
  ScenarioListResponse,
  ScenarioCreateResponse,
  ScenarioListQuery,
  Template,
  TemplateCreate,
  TemplateUpdate,
  TemplateListResponse,
  TemplateListQuery,
  Variable,
  VariableCreate,
  VariableUpdate,
  VariableListResponse,
  // Channel API types
  StartOAuthResponse,
  OAuthCallbackRequest,
  OAuthCallbackResponse,
  ManualConnectionRequest,
  ManualConnectionResponse,
  WhatsAppChannel,
  WhatsAppChannelListResponse,
  MessengerChannel,
  MessengerChannelListResponse,
  InstagramChannel,
  InstagramChannelListResponse,
  VapiChannel,
  VapiChannelListResponse,
  VoiceCompileResponse,
  VapiInspectResponse,
  VapiCallsResponse,
  VoiceSettingsUpdate,
  SMSChannel,
  SMSChannelListResponse,
  RegisterNumberResponse,
  RegistrationStatusResponse,
  UpdateChannelRequest,
  TestConnectionRequest,
  TestConnectionResponse,
  DiscoverResponse,
  LookupWabaResponse,
  LookupBusinessResponse,
  ConnectRequest,
  PoolNumbersResponse,
  AddPoolNumberResponse,
  CredentialCheckResponse,
  SaveCredentialsRequest,
  // Conversation API types
  ConversationListResponse,
  ConversationListQuery,
  ConversationDetailResponse,
  ConversationDetailQuery,
  ConversationMessagesResponse,
  MessageDebugInfo,
  ConversationMessagesQuery,
  ConversationTurnsResponse,
  ConversationTurnsQuery,
  ConversationAnalyticsResponse,
  ConversationAnalyticsQuery,
  ConversationSearchResponse,
  ConversationSearchRequest,
  ConversationExportFormat,
  SendMessageRequest,
  SendMessageResponse,
  // Interlocutor API types
  InterlocutorProfile,
  InterlocutorMessage,
  InterlocutorNote,
  ContactListResponse,
  ContactListQuery,
  ContactEngagementResponse,
  TicketListResponse,
  Ticket,
  TicketNote,
  TicketEvent,
} from '@/types';
import type {
  HandoffTicket,
  HandoffTicketListResponse,
} from '@/lib/handoffTickets';
import { scalekitAuthService } from './scalekit.service';

// Agent Config types
export interface AgentConfig {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  system_prompt: string | null;
  enabled: boolean;
  brain_type: string;
  settings: Record<string, unknown>;
  current_version: number;
  created_at: string;
  updated_at: string;
}

// Debug Info types for sandbox chat
export interface PhaseTiming {
  phase: string;
  duration_ms: number;
  start_offset_ms: number;
  skipped: boolean;
  skip_reason: string | null;
}

export interface SituationDebug {
  intent_label: string | null;
  intent_score: number | null;
  intent_changed: boolean;
  previous_intent: string | null;
  topic: string | null;
  topic_changed: boolean;
  tone: string | null;
  sentiment: string | null;
  frustration_level: string | null;
  urgency: string | null;
  language: string;
  scenario_signal: string | null;
  situation_facts: string[];
  candidate_variables: Record<string, { value: unknown; scope: string; is_update: boolean }>;
}

/** Per-fact match information for debugging */
export interface PerFactMatchDebug {
  fact: string;
  fact_index: number;
  score: number;
}

export interface ScoredRuleDebug {
  rule_id: string;
  rule_name: string;
  scope: string;
  score: number;
  source: string;
  /** Per-fact matches (when per-fact retrieval is enabled) */
  fact_matches?: PerFactMatchDebug[] | null;
  /** True if score was aggregated from multiple facts */
  aggregated_from_facts?: boolean;
}

export interface ScoredScenarioDebug {
  scenario_id: string;
  scenario_name: string;
  score: number;
}

export interface ScoredEpisodeDebug {
  episode_id: string;
  content: string;
  score: number;
  /** Full content length before truncation (0 = not truncated) */
  full_content_length?: number;
}

// Funnel debug interfaces - track filtering at each stage
export interface RulesFunnelDebug {
  total_in_db: number;
  after_business_filters: number;
  filtered_by_cooldown: number;
  filtered_by_max_fires: number;
  after_selection: number;
  filtered_by_score: number;
  after_llm_filter: number;
  filtered_by_llm: number;
  query_text: string | null;
  query_source: string | null; // situation_facts, new_intent_text, raw_message
}

export interface ScenariosFunnelDebug {
  total_in_db: number;
  after_selection: number;
  filtered_by_score: number;
}

export interface PipelineConfigDebug {
  // Embedding configuration
  embedding_provider: string | null;
  embedding_model: string | null;
  embedding_dimensions: number | null;
  // Vector backend
  vector_backend: string | null;
  distance_metric: string | null;
  // Rule selection
  rule_selection_strategy: string | null;
  rule_min_score: number | null;
  rule_max_k: number | null;
  rule_min_k: number | null;
  // Scenario selection
  scenario_selection_strategy: string | null;
  scenario_min_score: number | null;
  scenario_max_k: number | null;
  scenario_min_k: number | null;
  // Reranking
  rerank_provider: string | null;
  rerank_model: string | null;
  rerank_enabled: boolean;
  // Hybrid retrieval
  hybrid_enabled: boolean;
  hybrid_vector_weight: number | null;
  hybrid_bm25_weight: number | null;
  // Models per phase (for transparency)
  situation_sensor_model: string | null;
  rule_filtering_model: string | null;
  generation_model: string | null;
}

/** Retrieval timing breakdown for detailed performance analysis */
export interface RetrievalTimingDebug {
  embedding_time_ms: number;
  db_fetch_time_ms: number;
  vector_score_time_ms: number;
  bm25_time_ms: number;
  rerank_time_ms: number;
  business_filter_time_ms: number;
  selection_time_ms: number;
  total_time_ms: number;
  /** Per-fact retrieval timing (when enabled) */
  per_fact_embedding_ms?: number | null;
  per_fact_retrieval_ms?: number[] | null;
  aggregation_ms?: number | null;
  facts_processed?: number | null;
}

/** One rule candidate as scored and ranked BEFORE the selection cut
 * (Task 130 SEL commit 1) — includes candidates the selection dropped,
 * not just the ones that made it into `RetrievalDebug.rules`. Identifiers
 * and rule names only, no condition/action text, no client data. */
export interface RuleCandidateDebug {
  rule_id: string;
  rule_name: string;
  score: number;
  /** 1-based rank by score descending */
  rank: number;
  /** Whether the final selection kept this candidate */
  kept: boolean;
}

/** The full scored pool before the selection cut, plus the floor/bounds
 * actually applied — lets a trace reader recompute the cut without
 * reading the code. Optional: absent on records persisted before this
 * field existed, or when the retrieval result carries no candidate pool
 * (e.g. an older per-fact retrieval shape). Display is a separate phase
 * (Task 130 SEL brief) — this type only, no panel yet. */
export interface RulesCandidatesDebug {
  candidates: RuleCandidateDebug[];
  /** The EFFECTIVE floor the selection strategy applied — distinct from
   * `RetrievalConfigDebug.rule_min_score` (the configured vivier floor);
   * the two could diverge before Task 130 SEL commit 2. */
  effective_min_score: number | null;
  min_k: number | null;
  max_k: number | null;
}

export interface RetrievalDebug {
  rules_retrieved: number;
  scenarios_retrieved: number;
  memory_episodes_retrieved: number;
  retrieval_time_ms: number;
  rules: ScoredRuleDebug[];
  scenarios: ScoredScenarioDebug[];
  memory_episodes: ScoredEpisodeDebug[];
  // Funnel stats
  rules_funnel: RulesFunnelDebug | null;
  scenarios_funnel: ScenariosFunnelDebug | null;
  // Timing breakdown
  timing_breakdown: RetrievalTimingDebug | null;
  /** Candidates dropped by selection, with their scores (Task 130 SEL
   * commit 1) — optional, see RulesCandidatesDebug. */
  rules_candidates?: RulesCandidatesDebug | null;
}

export interface MatchedRuleDebug {
  rule_id: string;
  rule_name: string;
  scope: string;
  instruction: string;
  /** Full instruction length before truncation (0 = not truncated) */
  full_instruction_length?: number;
  match_score: number;
  relevance_score: number;
  reasoning: string;
  /** Facts that triggered this rule (per-fact retrieval) */
  triggering_facts?: string[] | null;
  /** Per-fact retrieval scores */
  per_fact_scores?: Record<string, number> | null;
  /** Number of facts that matched this rule */
  fact_match_count?: number;
}

export interface RejectedRuleDebug {
  rule_id: string;
  rule_name: string;
  scope: string;
  instruction: string;
  /** Full instruction length before truncation (0 = not truncated) */
  full_instruction_length?: number;
  applicability: string; // NOT_RELATED or UNSURE
  confidence: number;
  relevance: number;
  reasoning: string; // Critical - explains WHY the rule was rejected
}

export interface RuleMatchingDebug {
  rules_retrieved: number;
  rules_matched: number;
  rules_rejected: number;
  matched_rules: MatchedRuleDebug[];
  rejected_rules: RejectedRuleDebug[]; // New: rejected rules with full reasoning
  rejected_rule_ids: string[]; // Deprecated, use rejected_rules
}

/** One step transition recorded in the scenario instance history */
export interface TransitionRecordDebug {
  from_step_id: string | null;
  from_step_name: string | null;
  to_step_id: string;
  to_step_name: string | null;
  trigger: string;
  confidence: number;
  turn_number: number;
  timestamp: string;
}

/** Full history of a scenario instance across all turns */
export interface ScenarioInstanceHistoryDebug {
  /** ACTIVE | PAUSED | WAITING | COMPLETED | CANCELLED */
  instance_status: string;
  /** Step IDs currently active (>1 means parallel execution) */
  active_nodes: string[];
  transition_history: TransitionRecordDebug[];
  /** step_id → visit count (used for loop detection) */
  visited_steps: Record<string, number>;
  scenario_variables: Record<string, unknown>;
}

export interface ScenarioDebug {
  action: string;
  scenario_id: string | null;
  scenario_name: string | null;
  source_step_id: string | null;
  source_step_name: string | null;
  target_step_id: string | null;
  target_step_name: string | null;
  confidence: number;
  reasoning: string;
  was_relocalized: boolean;
  skipped_steps: string[];
  missing_profile_fields: string[];
  blocked_by_missing_fields: boolean;
  /** Full session journey — populated when debug=True */
  instance_history?: ScenarioInstanceHistoryDebug | null;
}

export interface ToolDebug {
  tool_name: string;
  tool_id: string | null;
  rule_id: string | null;
  success: boolean;
  execution_time_ms: number;
  inputs: Record<string, unknown>;
  outputs: Record<string, unknown> | null;
  error: string | null;
  timeout: boolean;
  blocked: boolean;
  blocked_reason: string | null;
  when: string | null;
  variables_filled: Record<string, unknown>;
}

/** A constraint from a rule applied to response planning */
export interface RuleConstraintDebug {
  rule_id: string;
  rule_name: string;
  constraint_type: string; // must_include, must_avoid, must_confirm
  text: string;
  priority?: number;
}

export interface ResponsePlanDebug {
  response_type: string;
  template_ids: string[];
  bullet_points: string[];
  must_include: string[];
  must_avoid: string[];
  scenario_contributions: Record<string, unknown>;
  /** Constraints with source rule attribution */
  constraints_from_rules?: RuleConstraintDebug[];
}

export interface GenerationDebug {
  model: string | null;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  generation_time_ms: number;
  template_used: string | null;
  template_mode: string | null;
  channel_formatted: boolean;
  channel: string | null;
  // Full prompt visibility for debugging
  system_prompt: string | null;
  user_messages: string[];
  /** Effect labels the model self-declared via its [EFFECTS: ...] suffix
   * (Task 130 SEL commit 1 bis) — labels only, never response text.
   * Optional: absent on records persisted before this field existed. */
  declared_effects?: string[];
}

/** A single provider cost line item for one LLM/embedding/voice request */
export interface CostLineItem {
  provider: string;
  kind: string;
  model: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  /** Fractional cents (e.g. 1.30 = $0.0130). Divide by 100 to get dollars. */
  cost_usd_cents: number | null;
  cost_is_estimate: boolean;
  request_id: string | null;
}

/** Aggregated cost for the whole turn */
export interface UsageCostDebug {
  /** Fractional cents — divide by 100 for dollars */
  turn_total_usd_cents: number;
  turn_total_is_estimate: boolean;
  line_items: CostLineItem[];
}

export interface ConstraintViolationDebug {
  rule_id: string;
  rule_name: string;
  violation_type: string;
  details: string;
  severity: string;
}

export interface EnforcementDebug {
  passed: boolean;
  violations: ConstraintViolationDebug[];
  regeneration_attempted: boolean;
  regeneration_succeeded: boolean;
  regeneration_attempts: number;
  fallback_used: boolean;
  fallback_template_id: string | null;
  enforcement_time_ms: number;
}

/** Session and conversation context for debugging */
export interface SessionContextDebug {
  session_id: string;
  turn_count: number;
  session_variables: Record<string, unknown>;
  previous_scenario_id: string | null;
  previous_step_id: string | null;
  user_message: string;
}

/** A missing scenario requirement for debugging */
export interface MissingRequirementDebug {
  field_name: string;
  filled: boolean;
  source: string; // profile, session, extraction, not_found
  confidence: number;
  needs_confirmation: boolean;
  required_level: string | null; // hard or soft
  fallback_action: string | null; // ask, skip, block, extract
  validation_errors: string[];
}

/** Task 130 PG1 — a configuration object asked about, written once per turn. */
export interface DecisionSubjectDebug {
  subject_type: 'rule' | 'scenario' | 'transition' | 'confirmation' | 'universal' | 'effect';
  subject_id?: string;
  subject_name?: string;
  /** The exact text sent to the model (the operator's own). */
  text: string;
  text_hash?: string;
}

/** Task 130 PG1 — one answered question of one call; its text is in `subjects[subject]`. */
export interface DecisionQuestionDebug {
  id: string;
  /** Key into `DecisionDebug.subjects`. */
  subject: string;
  /** Task 130 P7 — the option a `choice` selected; `score` is its probability. */
  choice?: string;
  score?: number;
  band?: 'below' | 'uncertain' | 'above';
  verdict?: 'applies' | 'does_not_apply';
  decided_by?: 'model' | 'doubt_rule' | 'second_opinion' | 'code';
  second_opinion?: Record<string, unknown>;
}

/** Task 130 PG1 — one call to the decision provider, in execution order. Null fields are omitted. */
export interface DecisionCallDebug {
  sequence: number;
  point: string;
  mode: 'shadow' | 'enforced';
  /** True when this result decided what was served; false for a shadow. */
  served: boolean;
  attempt: number;
  head: string;
  model: string;
  duration_ms: number;
  outcome: string;
  /** Questions this attempt carried; an attempt without answers lists none. */
  question_count: number;
  threshold_low?: number;
  threshold_high?: number;
  fallback?: { decided_by: 'legacy'; reason: string };
  questions: DecisionQuestionDebug[];
}

/** Task 130 PG1 — the turn's cognition profile and decision calls. Absent on a turn where every decision point is off. */
export interface DecisionDebug {
  profile: Record<string, 'off' | 'shadow' | 'enforced'>;
  shadow_sampled: boolean;
  config_fingerprint: string;
  provider_head?: string;
  subjects: Record<string, DecisionSubjectDebug>;
  calls: DecisionCallDebug[];
  divergences: { point: string; served: string[]; shadow: string[] }[];
}

export interface DebugInfo {
  turn_id: string;
  /** Session context showing state before this turn */
  session_context?: SessionContextDebug | null;
  phase_timings: PhaseTiming[];
  total_time_ms: number;
  situation: SituationDebug | null;
  retrieval: RetrievalDebug | null;
  rule_matching: RuleMatchingDebug | null;
  scenario: ScenarioDebug | null;
  tools: ToolDebug[];
  response_plan: ResponsePlanDebug | null;
  generation: GenerationDebug | null;
  enforcement: EnforcementDebug | null;
  /** Scenario fields that couldn't be filled */
  missing_requirements?: MissingRequirementDebug[];
  /** True if scenario entry was blocked due to missing hard requirements */
  scenario_blocked?: boolean;
  outcome_resolution: string | null;
  outcome_categories: string[];
  errors: string[];
  warnings: string[];
  /** Pipeline configuration used for this turn */
  pipeline_config?: PipelineConfigDebug | null;
  /** Per-request cost breakdown. Null when metering is disabled or data is unavailable. */
  cost?: UsageCostDebug | null;
  /** Task 130 PG1 — decision profile and calls; absent when every point is off. */
  decision?: DecisionDebug;
}

export interface AgentConfigUpdate {
  name?: string;
  description?: string;
  system_prompt?: string;
  enabled?: boolean;
}

// Vite proxy handles /api requests to backend
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Request timeout in milliseconds (fail fast when backend unavailable)
const REQUEST_TIMEOUT_MS = 15000;
// No timeout for LLM-based chat requests - let them run until completion
// Tool executions and complex pipelines can take variable time
const NO_TIMEOUT = 0;

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiClient {
  // Backend uses httpOnly cookies for authentication
  // No need to manage tokens on the client side

  // Tenant ID for Control API requests (set from AuthContext)
  private tenantId: string | null = null;

  getToken(): string {
    return 'session';
  }

  /**
   * Set the current tenant ID for Control API requests.
   * Should be called when organization changes in AuthContext.
   */
  setTenantId(tenantId: string | null): void {
    this.tenantId = tenantId;
  }

  /**
   * Get the current tenant ID.
   */
  getTenantId(): string | null {
    return this.tenantId;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
    timeoutMs: number = REQUEST_TIMEOUT_MS
  ): Promise<T> {
    const url = `${API_BASE_URL}${path}`;
    const isFormData = options.body instanceof FormData;
    const headers: HeadersInit = {
      // Don't set Content-Type for FormData — browser sets multipart boundary
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
    };

    // Add X-Tenant-ID header for Control API and Runtime API requests
    // Both APIs require tenant context for multi-tenant isolation
    if (path.startsWith('/control') || path.startsWith('/v1')) {
      // Use tenant ID if set, otherwise fall back to demo tenant in development
      const tenantId = this.tenantId || (import.meta.env.DEV ? '00000000-0000-0000-0000-000000000001' : null);
      if (tenantId) {
        (headers as Record<string, string>)['X-Tenant-ID'] = tenantId;
      }
    }

    // Backend uses httpOnly cookies, so we need to include credentials
    // No need to manually add Authorization header for cookie-based auth

    // Create abort controller for timeout (0 = no timeout)
    const controller = new AbortController();
    const timeoutId = timeoutMs > 0
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include', // This ensures cookies are sent with requests
        signal: timeoutMs > 0 ? controller.signal : undefined,
      });

      if (timeoutId) clearTimeout(timeoutId);

      if (response.status === 401) {
        // Try to refresh the token using cookies
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          // Retry the original request with new abort controller
          const retryController = new AbortController();
          const retryTimeoutId = timeoutMs > 0
            ? setTimeout(() => retryController.abort(), timeoutMs)
            : null;
          try {
            const retryResponse = await fetch(url, {
              ...options,
              headers,
              credentials: 'include',
              signal: timeoutMs > 0 ? retryController.signal : undefined,
            });
            if (retryTimeoutId) clearTimeout(retryTimeoutId);

            if (!retryResponse.ok) {
              const retryErrorText = await retryResponse.text();
              let retryErrorMessage = `API request failed: ${retryResponse.statusText}`;
              try {
                const retryErrorJson = JSON.parse(retryErrorText);
                if (retryErrorJson.detail) {
                  retryErrorMessage = typeof retryErrorJson.detail === 'string'
                    ? retryErrorJson.detail
                    : JSON.stringify(retryErrorJson.detail);
                } else if (retryErrorJson.message) {
                  retryErrorMessage = retryErrorJson.message;
                }
              } catch {
                if (retryErrorText && retryErrorText.length < 200) {
                  retryErrorMessage = retryErrorText;
                }
              }
              throw new ApiError(retryErrorMessage, retryResponse.status, retryErrorText);
            }

            return await retryResponse.json();
          } catch (retryError) {
            if (retryTimeoutId) clearTimeout(retryTimeoutId);
            if (retryError instanceof Error && retryError.name === 'AbortError') {
              throw new ApiError('Request timeout', 408);
            }
            throw retryError;
          }
        }
      }

      if (!response.ok) {
        // Try to parse error detail from response body
        const errorText = await response.text();
        let errorMessage = `API request failed: ${response.statusText}`;
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.detail) {
            errorMessage = typeof errorJson.detail === 'string'
              ? errorJson.detail
              : JSON.stringify(errorJson.detail);
          } else if (errorJson.message) {
            errorMessage = errorJson.message;
          }
        } catch {
          // If not JSON, use the raw text if available
          if (errorText && errorText.length < 200) {
            errorMessage = errorText;
          }
        }
        throw new ApiError(errorMessage, response.status, errorText);
      }

      // Handle empty responses
      const text = await response.text();
      return text ? JSON.parse(text) : {} as T;
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError('Request timeout', 408);
      }
      throw new ApiError(`Network error: ${error}`);
    }
  }

  private async refreshAccessToken(): Promise<boolean> {
    // Delegate to scalekitAuthService — it owns the canonical refresh URL
    // (relative `/auth/refresh`, which is what the proxy + cookie path
    // expect) and the single-flight coordination across the whole app.
    // The historical `${API_BASE_URL}/auth/refresh-token` path was wrong
    // on both counts: 404 (endpoint is `/auth/refresh`) and the `/api`
    // prefix doesn't strip in the proxy chain, so even the right path
    // wouldn't have routed correctly through `${API_BASE_URL}`.
    return scalekitAuthService.refreshSession();
  }

  /**
   * Generic GET request wrapper.
   * Returns response wrapped in { data: T } for consistency.
   */
  async get<T>(path: string): Promise<{ data: T }> {
    const result = await this.request<T>(path);
    return { data: result };
  }

  /**
   * Generic POST request wrapper.
   * Returns response wrapped in { data: T } for consistency.
   */
  async post<T>(path: string, body?: unknown): Promise<{ data: T }> {
    const result = await this.request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    return { data: result };
  }

  /**
   * Generic PATCH request wrapper.
   * Returns response wrapped in { data: T } for consistency.
   */
  async patch<T>(path: string, body?: unknown): Promise<{ data: T }> {
    const result = await this.request<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
    return { data: result };
  }

  /**
   * Generic DELETE request wrapper.
   * Returns response wrapped in { data: T } for consistency.
   */
  async delete<T = void>(path: string): Promise<{ data: T }> {
    const result = await this.request<T>(path, {
      method: 'DELETE',
    });
    return { data: result };
  }

  // Authentication endpoints
  auth = {
    login: async (email: string, password: string) => {
      const response = await this.request<{
        success: boolean;
        user: any;
        message?: string;
      }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      // Backend sets httpOnly cookies, no tokens in response body
      // Just return the response which contains user info
      return response;
    },

    register: async (data: {
      email: string;
      password: string;
      full_name: string;
      org_name?: string;
    }) => {
      return await this.request('/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    logout: async () => {
      await this.request('/auth/logout', {
        method: 'POST',
      });
    },

    getCurrentUser: async () => {
      return await this.request('/auth/me');
    },
  };

  // Agent endpoints (legacy - uses old agent model)
  agents = {
    list: async () => {
      return await this.request<Agent[]>('/user/agents');
    },

    get: async (id: string) => {
      return await this.request<Agent>(`/user/agents/${id}`);
    },

    create: async (data: Partial<Agent>) => {
      return await this.request<Agent>('/user/agents', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    update: async (id: string, data: Partial<Agent>) => {
      return await this.request<Agent>(`/user/agents/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    delete: async (id: string) => {
      return await this.request(`/user/agents/${id}`, {
        method: 'DELETE',
      });
    },

    chat: async (agentId: string, message: string) => {
      return await this.request<ChatMessage>(`/user/agents/${agentId}/chat`, {
        method: 'POST',
        body: JSON.stringify({ message }),
      });
    },
  };

  // Runtime agents - uses new template + override architecture
  runtimeAgents = {
    /**
     * List all resolved agents available to the tenant.
     * Returns agents with templates merged with any overrides.
     */
    list: async (params?: { roleId?: string; includeDisabled?: boolean }) => {
      const searchParams = new URLSearchParams();
      if (params?.roleId) searchParams.set('role_id', params.roleId);
      if (params?.includeDisabled) searchParams.set('include_disabled', 'true');
      const query = searchParams.toString();
      return await this.request<ResolvedAgentsResponse>(
        `/v1/agents${query ? `?${query}` : ''}`
      );
    },

    /**
     * Get a specific resolved agent by ID.
     */
    get: async (agentId: string) => {
      return await this.request<ResolvedAgent>(`/v1/agents/${agentId}`);
    },

    /**
     * Set overrides for a template agent.
     */
    setOverride: async (templateId: string, override: AgentOverride) => {
      return await this.request<{ status: string; template_id: string }>(
        `/v1/agents/${templateId}/overrides`,
        {
          method: 'PUT',
          body: JSON.stringify(override),
        }
      );
    },

    /**
     * Remove overrides for a template agent.
     */
    removeOverride: async (templateId: string) => {
      return await this.request<{ status: string; template_id: string }>(
        `/v1/agents/${templateId}/overrides`,
        { method: 'DELETE' }
      );
    },

    /**
     * Create a custom agent.
     */
    createCustom: async (agent: CustomAgentInput) => {
      return await this.request<ResolvedAgent>('/v1/agents/custom', {
        method: 'POST',
        body: JSON.stringify(agent),
      });
    },

    /**
     * Update a custom agent.
     */
    updateCustom: async (agentId: string, updates: Partial<CustomAgentInput>) => {
      return await this.request<ResolvedAgent>(`/v1/agents/custom/${agentId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
    },

    /**
     * Delete a custom agent.
     */
    deleteCustom: async (agentId: string) => {
      return await this.request<{ status: string; agent_id: string }>(
        `/v1/agents/custom/${agentId}`,
        { method: 'DELETE' }
      );
    },

    /**
     * Disable a template agent for all roles.
     */
    disable: async (templateId: string) => {
      return await this.request<{ status: string; template_id: string; disabled: boolean }>(
        `/v1/agents/${templateId}/disable`,
        { method: 'POST' }
      );
    },

    /**
     * Re-enable a previously disabled agent.
     */
    enable: async (templateId: string) => {
      return await this.request<{ status: string; template_id: string; disabled: boolean }>(
        `/v1/agents/${templateId}/enable`,
        { method: 'POST' }
      );
    },

    /**
     * Subscribe to a package.
     */
    subscribePackage: async (packageId: string) => {
      return await this.request<{ status: string; package_id: string }>(
        `/v1/agents/packages/${packageId}/subscribe`,
        { method: 'POST' }
      );
    },

    /**
     * Unsubscribe from a package.
     */
    unsubscribePackage: async (packageId: string) => {
      return await this.request<{ status: string; package_id: string }>(
        `/v1/agents/packages/${packageId}/unsubscribe`,
        { method: 'POST' }
      );
    },

    /**
     * Purchase an individual agent template.
     */
    purchase: async (templateId: string) => {
      return await this.request<{ status: string; template_id: string }>(
        `/v1/agents/${templateId}/purchase`,
        { method: 'POST' }
      );
    },
  };

  // Workflow endpoints
  workflows = {
    list: async () => {
      return await this.request<Workflow[]>('/user/workflows');
    },

    get: async (id: string) => {
      return await this.request<Workflow>(`/user/workflows/${id}`);
    },

    create: async (data: Partial<Workflow>) => {
      return await this.request<Workflow>('/user/workflows', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    update: async (id: string, data: Partial<Workflow>) => {
      return await this.request<Workflow>(`/user/workflows/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    delete: async (id: string) => {
      return await this.request(`/user/workflows/${id}`, {
        method: 'DELETE',
      });
    },

    execute: async (id: string, input?: any) => {
      return await this.request(`/user/workflows/${id}/execute`, {
        method: 'POST',
        body: JSON.stringify({ input }),
      });
    },
  };

  // Knowledge endpoints
  knowledge = {
    list: async () => {
      return await this.request<Knowledge[]>('/user/knowledge');
    },

    get: async (id: string) => {
      return await this.request<Knowledge>(`/user/knowledge/${id}`);
    },

    upload: async (file: File, metadata?: any) => {
      const formData = new FormData();
      formData.append('file', file);
      if (metadata) {
        formData.append('metadata', JSON.stringify(metadata));
      }

      const response = await fetch(`${API_BASE_URL}/user/knowledge/upload`, {
        method: 'POST',
        credentials: 'include', // Include cookies
        body: formData,
      });

      if (!response.ok) {
        throw new ApiError(
          `Upload failed: ${response.statusText}`,
          response.status
        );
      }

      return await response.json();
    },

    delete: async (id: string) => {
      return await this.request(`/user/knowledge/${id}`, {
        method: 'DELETE',
      });
    },

    search: async (query: string, limit = 10) => {
      return await this.request(`/user/knowledge/search`, {
        method: 'POST',
        body: JSON.stringify({ query, limit }),
      });
    },
  };

  // Chat endpoints
  chat = {
    getSessions: async () => {
      return await this.request('/user/chat/sessions');
    },

    getSession: async (sessionId: string) => {
      return await this.request(`/user/chat/sessions/${sessionId}`);
    },

    getMessages: async (sessionId: string) => {
      return await this.request(`/user/chat/sessions/${sessionId}/messages`);
    },

    sendMessage: async (sessionId: string, message: string, agentId?: string) => {
      return await this.request(`/user/chat/sessions/${sessionId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ message, agent_id: agentId }),
      });
    },
  };

  // Voice endpoints
  voice = {
    createCall: async (phoneNumber: string, agentId: string) => {
      return await this.request('/user/voice/call', {
        method: 'POST',
        body: JSON.stringify({ phone_number: phoneNumber, agent_id: agentId }),
      });
    },

    endCall: async (callId: string) => {
      return await this.request(`/user/voice/call/${callId}/end`, {
        method: 'POST',
      });
    },

    getCallStatus: async (callId: string) => {
      return await this.request(`/user/voice/call/${callId}/status`);
    },

    getTranscript: async (callId: string) => {
      return await this.request(`/user/voice/call/${callId}/transcript`);
    },
  };

  // Control API - Rules endpoints
  rules = {
    list: async (agentId: string, params?: RuleListQuery) => {
      const searchParams = new URLSearchParams();
      if (params?.scope) searchParams.set('scope', params.scope);
      if (params?.scope_id) searchParams.set('scope_id', params.scope_id);
      if (params?.enabled_only !== undefined) searchParams.set('enabled_only', String(params.enabled_only));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.offset) searchParams.set('offset', String(params.offset));
      const query = searchParams.toString();
      return await this.request<RuleListResponse>(
        `/control/v1/agents/${agentId}/rules${query ? `?${query}` : ''}`
      );
    },

    get: async (agentId: string, ruleId: string) => {
      return await this.request<Rule>(
        `/control/v1/agents/${agentId}/rules/${ruleId}`
      );
    },

    create: async (agentId: string, data: RuleCreate) => {
      return await this.request<RuleCreateResponse>(
        `/control/v1/agents/${agentId}/rules`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    update: async (agentId: string, ruleId: string, data: RuleUpdate) => {
      return await this.request<Rule>(
        `/control/v1/agents/${agentId}/rules/${ruleId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      );
    },

    delete: async (agentId: string, ruleId: string) => {
      return await this.request<void>(
        `/control/v1/agents/${agentId}/rules/${ruleId}`,
        {
          method: 'DELETE',
        }
      );
    },
  };

  // Control API - Scenarios endpoints
  scenarios = {
    list: async (agentId: string, params?: ScenarioListQuery) => {
      const searchParams = new URLSearchParams();
      if (params?.enabled_only !== undefined) searchParams.set('enabled_only', String(params.enabled_only));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.offset) searchParams.set('offset', String(params.offset));
      const query = searchParams.toString();
      return await this.request<ScenarioListResponse>(
        `/control/v1/agents/${agentId}/scenarios${query ? `?${query}` : ''}`
      );
    },

    get: async (agentId: string, scenarioId: string) => {
      return await this.request<Scenario>(
        `/control/v1/agents/${agentId}/scenarios/${scenarioId}`
      );
    },

    create: async (agentId: string, data: ScenarioCreate) => {
      return await this.request<ScenarioCreateResponse>(
        `/control/v1/agents/${agentId}/scenarios`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    update: async (agentId: string, scenarioId: string, data: ScenarioUpdate) => {
      return await this.request<Scenario>(
        `/control/v1/agents/${agentId}/scenarios/${scenarioId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      );
    },

    delete: async (agentId: string, scenarioId: string) => {
      return await this.request<void>(
        `/control/v1/agents/${agentId}/scenarios/${scenarioId}`,
        {
          method: 'DELETE',
        }
      );
    },
  };

  // Control API - Templates endpoints
  templates = {
    list: async (agentId: string, params?: TemplateListQuery) => {
      const searchParams = new URLSearchParams();
      if (params?.scope) searchParams.set('scope', params.scope);
      if (params?.scope_id) searchParams.set('scope_id', params.scope_id);
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.offset) searchParams.set('offset', String(params.offset));
      const query = searchParams.toString();
      return await this.request<TemplateListResponse>(
        `/control/v1/agents/${agentId}/templates${query ? `?${query}` : ''}`
      );
    },

    get: async (agentId: string, templateId: string) => {
      return await this.request<Template>(
        `/control/v1/agents/${agentId}/templates/${templateId}`
      );
    },

    create: async (agentId: string, data: TemplateCreate) => {
      return await this.request<Template>(
        `/control/v1/agents/${agentId}/templates`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    update: async (agentId: string, templateId: string, data: TemplateUpdate) => {
      return await this.request<Template>(
        `/control/v1/agents/${agentId}/templates/${templateId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      );
    },

    delete: async (agentId: string, templateId: string) => {
      return await this.request<void>(
        `/control/v1/agents/${agentId}/templates/${templateId}`,
        {
          method: 'DELETE',
        }
      );
    },
  };

  // Control API - Variables endpoints
  variables = {
    list: async (agentId: string) => {
      return await this.request<VariableListResponse>(
        `/control/v1/agents/${agentId}/variables`
      );
    },

    get: async (agentId: string, variableId: string) => {
      return await this.request<Variable>(
        `/control/v1/agents/${agentId}/variables/${variableId}`
      );
    },

    create: async (agentId: string, data: VariableCreate) => {
      return await this.request<Variable>(
        `/control/v1/agents/${agentId}/variables`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    update: async (agentId: string, variableId: string, data: VariableUpdate) => {
      return await this.request<Variable>(
        `/control/v1/agents/${agentId}/variables/${variableId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      );
    },

    delete: async (agentId: string, variableId: string) => {
      return await this.request<void>(
        `/control/v1/agents/${agentId}/variables/${variableId}`,
        {
          method: 'DELETE',
        }
      );
    },
  };

  // Control API - Agent Config endpoints
  agentConfig = {
    get: async (agentId: string): Promise<AgentConfig> => {
      return await this.request<AgentConfig>(
        `/control/v1/agents/${agentId}`
      );
    },

    update: async (agentId: string, data: AgentConfigUpdate): Promise<AgentConfig> => {
      return await this.request<AgentConfig>(
        `/control/v1/agents/${agentId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      );
    },
  };

  // Agent Profile endpoints (brand identity, personality, voice, etc.)
  agentProfile = {
    get: async (agentId: string) => {
      return await this.request<{ agent_id: string; profile: Record<string, unknown> }>(
        `/control/v1/agents/${agentId}/profile`
      );
    },

    update: async (agentId: string, data: Record<string, unknown>) => {
      // ``sync_results`` reports per-channel outcomes for the inline sync the
      // backend ran during this PATCH (Vapi voice push, WhatsApp profile,
      // Messenger greeting). ``"ok"`` / ``"skipped"`` / ``"no_changes"`` are
      // good; anything starting with ``"error:"`` or ``"timeout"`` means the
      // local DB save succeeded but the channel did NOT pick up the change.
      return await this.request<{
        agent_id: string;
        profile: Record<string, unknown>;
        sync_results?: Record<string, string>;
      }>(
        `/control/v1/agents/${agentId}/profile`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      );
    },

    uploadAvatar: async (agentId: string, file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return await this.request<{ avatar_url: string; agent_id: string }>(
        `/control/v1/agents/${agentId}/profile/avatar`,
        {
          method: 'POST',
          body: formData,
        }
      );
    },
  };

  // Control API - Offerings (products, services, menu items)
  offerings = {
    list: async (agentId: string, params?: { category?: string; available_only?: boolean; limit?: number; offset?: number }) => {
      const sp = new URLSearchParams();
      if (params?.category) sp.set('category', params.category);
      if (params?.available_only !== undefined) sp.set('available_only', String(params.available_only));
      if (params?.limit) sp.set('limit', String(params.limit));
      if (params?.offset) sp.set('offset', String(params.offset));
      const qs = sp.toString();
      return await this.request<{ offerings: Array<Record<string, unknown>>; total: number }>(
        `/control/v1/agents/${agentId}/offerings${qs ? `?${qs}` : ''}`
      );
    },

    create: async (agentId: string, data: Record<string, unknown>) => {
      return await this.request<Record<string, unknown>>(
        `/control/v1/agents/${agentId}/offerings`,
        { method: 'POST', body: JSON.stringify(data) }
      );
    },

    update: async (agentId: string, offeringId: string, data: Record<string, unknown>) => {
      return await this.request<Record<string, unknown>>(
        `/control/v1/agents/${agentId}/offerings/${offeringId}`,
        { method: 'PATCH', body: JSON.stringify(data) }
      );
    },

    delete: async (agentId: string, offeringId: string) => {
      return await this.request<void>(
        `/control/v1/agents/${agentId}/offerings/${offeringId}`,
        { method: 'DELETE' }
      );
    },

    bulkCreate: async (agentId: string, offerings: Array<Record<string, unknown>>) => {
      return await this.request<{ created: number; ids: string[] }>(
        `/control/v1/agents/${agentId}/offerings/bulk`,
        { method: 'POST', body: JSON.stringify({ offerings }) }
      );
    },
  };

  // Control API - Orders (Task 082 - cart + quote read/admin surface)
  orders = {
    list: async (
      agentId: string,
      params?: {
        state?: string;
        interlocutor_id?: string;
        limit?: number;
        offset?: number;
      }
    ) => {
      const sp = new URLSearchParams();
      if (params?.state) sp.set('state', params.state);
      if (params?.interlocutor_id) sp.set('interlocutor_id', params.interlocutor_id);
      if (params?.limit !== undefined) sp.set('limit', String(params.limit));
      if (params?.offset !== undefined) sp.set('offset', String(params.offset));
      const qs = sp.toString();
      return await this.request<{
        total: number;
        limit: number;
        offset: number;
        orders: Array<Record<string, unknown>>;
      }>(`/control/v1/agents/${agentId}/orders${qs ? `?${qs}` : ''}`);
    },

    get: async (agentId: string, orderId: string) => {
      return await this.request<Record<string, unknown>>(
        `/control/v1/agents/${agentId}/orders/${orderId}`
      );
    },

    cancel: async (agentId: string, orderId: string, reason?: string) => {
      return await this.request<Record<string, unknown>>(
        `/control/v1/agents/${agentId}/orders/${orderId}/cancel`,
        { method: 'PATCH', body: JSON.stringify({ reason: reason ?? null }) }
      );
    },

    markPaid: async (
      agentId: string,
      orderId: string,
      payload: { stripe_session_id: string; stripe_payment_intent_id?: string }
    ) => {
      return await this.request<Record<string, unknown>>(
        `/control/v1/agents/${agentId}/orders/${orderId}/mark-paid`,
        { method: 'PATCH', body: JSON.stringify(payload) }
      );
    },
  };

  /**
   * Demandes de rappel (Task 129 W-EB).
   *
   * Les tickets sont ÉCRITS par `support.create_handoff_ticket` côté runtime ;
   * cette famille ne fait que les lire et les traiter. Aucune création ici :
   * une demande de rappel est un fait de la conversation, pas de la console.
   */
  handoffTickets = {
    list: async (
      agentId: string,
      params?: { status?: string; limit?: number; cursor?: string }
    ) => {
      const sp = new URLSearchParams();
      if (params?.status) sp.set('status', params.status);
      if (params?.limit !== undefined) sp.set('limit', String(params.limit));
      if (params?.cursor) sp.set('cursor', params.cursor);
      const qs = sp.toString();
      return await this.request<HandoffTicketListResponse>(
        `/control/v1/agents/${agentId}/handoff-tickets${qs ? `?${qs}` : ''}`
      );
    },

    get: async (agentId: string, ticketId: string) => {
      return await this.request<HandoffTicket>(
        `/control/v1/agents/${agentId}/handoff-tickets/${ticketId}`
      );
    },

    triage: async (
      agentId: string,
      ticketId: string,
      payload: { status?: string; internal_note?: string }
    ) => {
      return await this.request<HandoffTicket>(
        `/control/v1/agents/${agentId}/handoff-tickets/${ticketId}`,
        { method: 'PATCH', body: JSON.stringify(payload) }
      );
    },
  };

  // Control API - Knowledge Base (Task 066 — RAG documents)
  knowledge = {
    listDocuments: async (
      params?: { agentId?: string; limit?: number; offset?: number }
    ) => {
      const sp = new URLSearchParams();
      if (params?.agentId) sp.set('agent_id', params.agentId);
      if (params?.limit) sp.set('limit', String(params.limit));
      if (params?.offset !== undefined) sp.set('offset', String(params.offset));
      const qs = sp.toString();
      return await this.request<{
        documents: Array<{
          id: string;
          agent_id: string | null;
          filename: string;
          file_type: string;
          size_bytes: number;
          parse_status: string;
          parse_error: string | null;
          chunk_count: number;
          metadata: Record<string, unknown>;
          created_at: string;
          updated_at: string;
        }>;
        total: number;
        limit: number;
        offset: number;
      }>(`/control/v1/knowledge/documents${qs ? `?${qs}` : ''}`);
    },

    uploadDocument: async (file: File, agentId?: string) => {
      const formData = new FormData();
      formData.append('file', file);
      if (agentId) formData.append('agent_id', agentId);
      return await this.request<{
        document: {
          id: string;
          agent_id: string | null;
          filename: string;
          file_type: string;
          size_bytes: number;
          parse_status: string;
          parse_error: string | null;
          chunk_count: number;
          metadata: Record<string, unknown>;
          created_at: string;
          updated_at: string;
        };
        chunk_count: number;
        duration_ms: number;
      }>(`/control/v1/knowledge/documents/upload`, {
        method: 'POST',
        body: formData,
      });
    },

    deleteDocument: async (documentId: string) => {
      return await this.request<void>(
        `/control/v1/knowledge/documents/${documentId}`,
        { method: 'DELETE' }
      );
    },

    getDocumentStatus: async (documentId: string) => {
      return await this.request<{
        id: string;
        parse_status: string;
        parse_error: string | null;
        chunk_count: number;
      }>(`/control/v1/knowledge/documents/${documentId}/status`);
    },
  };

  // Control API - Tool Integrations (catalog, connections, activations)
  integrations = {
    listCatalog: async () => {
      return await this.request<Array<{
        id: string;
        provider: string;
        name: string;
        display_name: string;
        description: string | null;
        category: string;
        auth_type: string;
        risk_level: string;
        status: string;
        nango_integration: string | null;
        icon_url: string | null;
      }>>('/control/v1/tools/catalog');
    },

    listConnections: async () => {
      return await this.request<Array<{
        id: string;
        tenant_id: string;
        catalog_entry_id: string;
        tool_name: string | null;
        nango_connection_id: string | null;
        health_status: string;
        health_error: string | null;
        config_overrides: Record<string, unknown>;
      }>>('/control/v1/tools/connections');
    },

    initiateConnection: async (catalogEntryId: string) => {
      return await this.request<{
        connections: Array<{
          id: string;
          tenant_id: string;
          catalog_entry_id: string;
          tool_name: string | null;
          nango_connection_id: string | null;
          health_status: string;
          health_error: string | null;
          config_overrides: Record<string, unknown>;
        }>;
        oauth_url: string;
      }>('/control/v1/tools/connections/initiate', {
        method: 'POST',
        body: JSON.stringify({ catalog_entry_id: catalogEntryId }),
      });
    },

    deleteConnection: async (connectionId: string) => {
      return await this.request<void>(
        `/control/v1/tools/connections/${connectionId}`,
        { method: 'DELETE' }
      );
    },

    verifyConnection: async (nangoConnectionId: string, agentId?: string) => {
      const payload: Record<string, string> = { nango_connection_id: nangoConnectionId };
      if (agentId) payload.agent_id = agentId;
      return await this.request<{
        health_status: string;
        updated_count: number;
        activations_created: number;
      }>('/control/v1/tools/connections/verify', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    listActivations: async (agentId: string) => {
      return await this.request<Array<{
        id: string;
        tenant_id: string;
        agent_id: string;
        connection_id: string | null;
        catalog_entry_id: string | null;
        enabled: boolean;
        permission_level: string;
      }>>(`/control/v1/tools/activations/${agentId}`);
    },

    activateTool: async (agentId: string, connectionId: string) => {
      return await this.request<{
        id: string;
        tenant_id: string;
        agent_id: string;
        connection_id: string | null;
        catalog_entry_id: string | null;
        enabled: boolean;
        permission_level: string;
      }>(`/control/v1/tools/activations/${agentId}`, {
        method: 'POST',
        body: JSON.stringify({ connection_id: connectionId }),
      });
    },

    deactivateTool: async (agentId: string, activationId: string) => {
      return await this.request<void>(
        `/control/v1/tools/activations/${agentId}/${activationId}`,
        { method: 'DELETE' }
      );
    },
  };

  // Voice catalog (curated, provider-agnostic voice library)
  voices = {
    list: async (params?: { gender?: string; personality?: string; accent?: string; language?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.gender) searchParams.set('gender', params.gender);
      if (params?.personality) searchParams.set('personality', params.personality);
      if (params?.accent) searchParams.set('accent', params.accent);
      if (params?.language) searchParams.set('language', params.language);
      const qs = searchParams.toString();
      return await this.request<{
        voices: Array<Record<string, unknown>>;
        filters: { genders: string[]; personalities: string[]; accents: string[]; languages: string[] };
        presets: Record<string, unknown>;
      }>(`/control/v1/voices${qs ? `?${qs}` : ''}`);
    },

    get: async (voiceId: string) => {
      return await this.request<{ voice: Record<string, unknown> }>(
        `/control/v1/voices/${voiceId}`
      );
    },
  };

  // Control API - Activation endpoints (activate templates into runtime agents)
  activation = {
    /**
     * Get available templates based on tenant subscription.
     */
    getAvailable: async () => {
      return await this.request<{
        templates: Array<{
          id: string;
          name: string;
          type: string;
          description: string;
          theme_color: string;
          icon: string;
          avatar_url: string | null;
          status: string;
          is_subscribed: boolean;
        }>;
        total: number;
      }>('/control/v1/agents/available');
    },

    /**
     * Get activated (runtime) agents for the tenant.
     */
    getActive: async (params?: { enabledOnly?: boolean; limit?: number; offset?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.enabledOnly !== undefined) searchParams.set('enabled_only', String(params.enabledOnly));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.offset) searchParams.set('offset', String(params.offset));
      const query = searchParams.toString();
      return await this.request<{
        agents: Array<{
          id: string;
          name: string;
          description: string | null;
          system_prompt: string | null;
          enabled: boolean;
          brain_type: string;
          theme_color: string | null;
          template_id: string | null;
          created_at: string;
          updated_at: string;
        }>;
        total: number;
      }>(`/control/v1/agents/active${query ? `?${query}` : ''}`);
    },

    /**
     * Activate a template into a runtime agent.
     */
    activate: async (templateId: string) => {
      return await this.request<{
        id: string;
        tenant_id: string;
        name: string;
        description: string | null;
        system_prompt: string | null;
        enabled: boolean;
        brain_type: string;
        template_id: string;
        created_at: string;
        updated_at: string;
      }>('/control/v1/agents/activate', {
        method: 'POST',
        body: JSON.stringify({ template_id: templateId }),
      });
    },

    /**
     * Deactivate (soft-delete) a runtime agent.
     */
    deactivate: async (agentId: string) => {
      return await this.request<void>(`/control/v1/agents/${agentId}/deactivate`, {
        method: 'POST',
      });
    },
  };

  // Control API - Unified Channels endpoint (all types in one call)
  channels = {
    /**
     * List all channels for an agent across all channel types.
     * Single request replaces 5 separate per-type calls.
     */
    listAll: async (agentId: string) => {
      return await this.request<{
        whatsapp: any[];
        messenger: any[];
        instagram: any[];
        vapi: any[];
        sms: any[];
      }>(`/control/v1/channels/all?agent_id=${encodeURIComponent(agentId)}`);
    },
  };

  // Control API - WhatsApp Channels endpoints
  whatsappChannels = {
    /**
     * Start OAuth flow for WhatsApp Embedded Signup.
     * Returns configuration for FB.login() popup.
     */
    startOAuth: async (agentId: string) => {
      return await this.request<StartOAuthResponse>(
        '/control/v1/channels/whatsapp/oauth/start',
        {
          method: 'POST',
          body: JSON.stringify({ agent_id: agentId }),
        }
      );
    },

    /**
     * Complete OAuth callback after FB.login() popup.
     * Exchanges short-lived token for long-lived token and creates channel.
     */
    completeOAuth: async (data: OAuthCallbackRequest) => {
      return await this.request<OAuthCallbackResponse>(
        '/control/v1/channels/whatsapp/oauth/callback',
        {
          method: 'POST',
          body: JSON.stringify(data),
        },
        30000, // 30s - exchanges token with Meta + stores in Nango
      );
    },

    /**
     * Create channel with manual connection (advanced users).
     */
    manualConnect: async (data: ManualConnectionRequest) => {
      return await this.request<ManualConnectionResponse>(
        '/control/v1/channels/whatsapp/manual',
        {
          method: 'POST',
          body: JSON.stringify(data),
        },
        30000, // 30s - calls Meta API + Nango + DB
      );
    },

    /**
     * Discover WABAs and phone numbers from an access token.
     * Returns all accounts the token has access to.
     */
    discover: async (accessToken: string, tokenSource: 'system_user' | 'oauth' = 'system_user') => {
      return await this.request<DiscoverResponse>(
        '/control/v1/channels/whatsapp/discover',
        {
          method: 'POST',
          body: JSON.stringify({ access_token: accessToken, token_source: tokenSource }),
        },
        30000, // 30s - introspects token + fetches all WABAs/phones
      );
    },

    /**
     * Look up a specific WABA by ID when auto-discovery can't enumerate.
     * Used when discover() returns requires_waba_id=true.
     */
    lookupWaba: async (accessToken: string, wabaId: string) => {
      return await this.request<LookupWabaResponse>(
        '/control/v1/channels/whatsapp/lookup-waba',
        {
          method: 'POST',
          body: JSON.stringify({ access_token: accessToken, waba_id: wabaId }),
        },
        15000, // 15s - validates one WABA + lists phones
      );
    },

    /**
     * Look up all WABAs under a Meta Business portfolio.
     * Alternative to lookupWaba — user provides business_id instead of waba_id.
     */
    lookupBusiness: async (accessToken: string, businessId: string) => {
      return await this.request<LookupBusinessResponse>(
        '/control/v1/channels/whatsapp/lookup-business',
        {
          method: 'POST',
          body: JSON.stringify({ access_token: accessToken, business_id: businessId }),
        },
        20000, // 20s - enumerates all WABAs + phones per WABA
      );
    },

    /**
     * Connect a specific WABA + phone number using a validated token.
     * Call after discover() — user picks WABA and phone from dropdowns.
     */
    connect: async (data: ConnectRequest) => {
      return await this.request<WhatsAppChannel>(
        '/control/v1/channels/whatsapp/connect',
        {
          method: 'POST',
          body: JSON.stringify(data),
        },
        30000, // 30s - exchanges token + stores + creates records + subscribes webhooks
      );
    },

    /**
     * List available Twilio phone numbers from the pool.
     */
    listPoolNumbers: async () => {
      return await this.request<PoolNumbersResponse>(
        '/control/v1/channels/whatsapp/pool-numbers',
      );
    },

    /**
     * Register a Twilio pool number on a WABA.
     * Adds to WABA, OTP verifies via Twilio, and registers for WhatsApp Cloud API.
     * Takes 30-60s due to OTP polling.
     */
    addPoolNumber: async (
      wabaId: string,
      poolNumberId: string,
      options?: { accessToken?: string; useStoredCredentials?: boolean },
    ) => {
      return await this.request<AddPoolNumberResponse>(
        '/control/v1/channels/whatsapp/add-pool-number',
        {
          method: 'POST',
          body: JSON.stringify({
            ...(options?.accessToken ? { access_token: options.accessToken } : {}),
            use_stored_credentials: options?.useStoredCredentials ?? false,
            waba_id: wabaId,
            pool_number_id: poolNumberId,
          }),
        },
        120000, // 120s - OTP polling can take up to 60s + Meta API calls
      );
    },

    /**
     * Check if tenant has stored Meta admin credentials.
     */
    checkCredentials: async () => {
      return await this.request<CredentialCheckResponse>(
        '/control/v1/channels/whatsapp/credentials',
      );
    },

    /**
     * Discover WABAs using stored tenant credentials (no token in request).
     */
    discoverStored: async (businessId?: string) => {
      return await this.request<DiscoverResponse>(
        '/control/v1/channels/whatsapp/discover-stored',
        {
          method: 'POST',
          body: JSON.stringify({ business_id: businessId ?? null }),
        },
        30000,
      );
    },

    /**
     * Save or update Meta admin credentials for the tenant.
     */
    saveCredentials: async (data: SaveCredentialsRequest) => {
      return await this.request<CredentialCheckResponse>(
        '/control/v1/channels/whatsapp/credentials',
        {
          method: 'PUT',
          body: JSON.stringify(data),
        },
      );
    },

    /**
     * List all WhatsApp channels for the tenant.
     */
    list: async (agentId?: string) => {
      const searchParams = new URLSearchParams();
      if (agentId) searchParams.set('agent_id', agentId);
      const query = searchParams.toString();
      return await this.request<WhatsAppChannelListResponse>(
        `/control/v1/channels/whatsapp${query ? `?${query}` : ''}`
      );
    },

    /**
     * Get a specific WhatsApp channel.
     */
    get: async (channelId: string) => {
      return await this.request<WhatsAppChannel>(
        `/control/v1/channels/whatsapp/${channelId}`
      );
    },

    /**
     * Update a WhatsApp channel.
     */
    update: async (channelId: string, data: UpdateChannelRequest) => {
      return await this.request<WhatsAppChannel>(
        `/control/v1/channels/whatsapp/${channelId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      );
    },

    /**
     * Delete a WhatsApp channel.
     */
    delete: async (channelId: string) => {
      return await this.request<void>(
        `/control/v1/channels/whatsapp/${channelId}`,
        {
          method: 'DELETE',
        }
      );
    },

    /**
     * Test WhatsApp connection by sending a test message.
     */
    testConnection: async (channelId: string, data: TestConnectionRequest) => {
      return await this.request<TestConnectionResponse>(
        `/control/v1/channels/whatsapp/${channelId}/test`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    /**
     * Refresh channel health by re-fetching status from Meta API.
     * Returns updated channel with name_status, quality_rating, etc.
     */
    refreshHealth: async (channelId: string) => {
      return await this.request<WhatsAppChannel>(
        `/control/v1/channels/whatsapp/${channelId}/refresh`,
        { method: 'POST' }
      );
    },

    /**
     * Register an agent's phone number with WhatsApp Business API.
     * Adds the number to the WABA and requests OTP verification.
     */
    registerNumber: async (agentId: string) => {
      return await this.request<RegisterNumberResponse>(
        `/control/v1/channels/whatsapp/register/${agentId}`,
        { method: 'POST' },
        120000,
      );
    },

    /**
     * Auto-verify a WhatsApp number registration by polling for the OTP.
     */
    autoVerify: async (registrationId: string) => {
      return await this.request<RegistrationStatusResponse>(
        `/control/v1/channels/whatsapp/register/${registrationId}/auto-verify`,
        { method: 'POST' },
        120000,
      );
    },

    /**
     * Manually verify a WhatsApp number registration with a user-provided OTP code.
     */
    verifyCode: async (registrationId: string, code: string) => {
      return await this.request<RegistrationStatusResponse>(
        `/control/v1/channels/whatsapp/register/${registrationId}/verify`,
        {
          method: 'POST',
          body: JSON.stringify({ code }),
        },
      );
    },

  };

  // Control API - Messenger Channels endpoints
  messengerChannels = {
    /**
     * Start OAuth flow for Messenger.
     * Returns Nango OAuth URL to redirect user to.
     */
    startOAuth: async (agentId: string) => {
      return await this.request<{ oauth_url: string; connection_id: string }>(
        `/control/v1/messenger/${agentId}/start-oauth`,
        { method: 'POST' }
      );
    },

    /**
     * Complete OAuth callback after user returns from Nango.
     * Creates the Messenger channel.
     */
    completeOAuth: async (agentId: string, data: { connection_id: string; page_id: string; page_name: string }) => {
      return await this.request<MessengerChannel>(
        `/control/v1/messenger/${agentId}/callback`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    /**
     * List available Facebook Pages after OAuth.
     */
    listPages: async (connectionId: string) => {
      return await this.request<Array<{ id: string; name: string; access_token: string; category: string }>>(
        `/control/v1/messenger/pages?connection_id=${connectionId}`
      );
    },

    /**
     * List all Messenger channels for an agent.
     */
    list: async (agentId: string) => {
      return await this.request<MessengerChannelListResponse>(
        `/control/v1/messenger/${agentId}/channels`
      );
    },

    /**
     * Delete a Messenger channel.
     */
    delete: async (agentId: string, channelId: string) => {
      return await this.request<void>(
        `/control/v1/messenger/${agentId}/channels/${channelId}`,
        { method: 'DELETE' }
      );
    },
  };

  // Control API - Instagram Channels endpoints
  instagramChannels = {
    /**
     * Start OAuth flow for Instagram DM.
     * Returns Nango OAuth URL to redirect user to.
     */
    startOAuth: async (agentId: string) => {
      return await this.request<{ oauth_url: string; connection_id: string }>(
        `/control/v1/instagram/${agentId}/start-oauth`,
        { method: 'POST' }
      );
    },

    /**
     * Complete OAuth callback after user returns from Nango.
     * Creates the Instagram channel.
     */
    completeOAuth: async (agentId: string, data: {
      connection_id: string;
      instagram_account_id: string;
      instagram_username: string;
      page_id: string;
    }) => {
      return await this.request<InstagramChannel>(
        `/control/v1/instagram/${agentId}/callback`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    /**
     * List available Instagram accounts after OAuth.
     */
    listAccounts: async (connectionId: string) => {
      return await this.request<Array<{
        instagram_account_id: string;
        username: string;
        profile_picture_url: string;
        followers_count: number;
        page_id: string;
        page_name: string;
      }>>(
        `/control/v1/instagram/accounts?connection_id=${connectionId}`
      );
    },

    /**
     * List all Instagram channels for an agent.
     */
    list: async (agentId: string) => {
      return await this.request<InstagramChannelListResponse>(
        `/control/v1/instagram/${agentId}/channels`
      );
    },

    /**
     * Delete an Instagram channel.
     */
    delete: async (agentId: string, channelId: string) => {
      return await this.request<void>(
        `/control/v1/instagram/${agentId}/channels/${channelId}`,
        { method: 'DELETE' }
      );
    },
  };

  // Control API - Vapi Voice Channels endpoints
  vapiChannels = {
    /**
     * Create a Vapi voice channel for an agent.
     * Auto-provisions a Vapi assistant using the platform API key.
     */
    create: async (agentId: string) => {
      // Voice provisioning creates a Vapi assistant, compiles rules, and
      // allocates a phone number — allow up to 60s for the full flow.
      return await this.request<VapiChannel>(
        '/control/v1/channels/vapi',
        {
          method: 'POST',
          body: JSON.stringify({ agent_id: agentId }),
        },
        60000
      );
    },

    /**
     * List all Vapi voice channels for the tenant.
     */
    list: async (agentId?: string) => {
      const searchParams = new URLSearchParams();
      if (agentId) searchParams.set('agent_id', agentId);
      const query = searchParams.toString();
      return await this.request<VapiChannelListResponse>(
        `/control/v1/channels/vapi${query ? `?${query}` : ''}`
      );
    },

    /**
     * Get a specific Vapi voice channel.
     */
    get: async (channelId: string) => {
      return await this.request<VapiChannel>(
        `/control/v1/channels/vapi/${channelId}`
      );
    },

    /**
     * Update a Vapi voice channel.
     */
    update: async (channelId: string, data: { is_active?: boolean; assistant_id?: string; phone_number_id?: string }) => {
      return await this.request<VapiChannel>(
        `/control/v1/channels/vapi/${channelId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      );
    },

    /**
     * Delete a Vapi voice channel.
     */
    delete: async (channelId: string) => {
      return await this.request<void>(
        `/control/v1/channels/vapi/${channelId}`,
        {
          method: 'DELETE',
        }
      );
    },

    /**
     * Compile voice configuration for an agent.
     * Returns classified rules and a voice-optimized system prompt.
     * Read-only analysis — does not modify the agent or push to Vapi.
     */
    compile: async (agentId: string) => {
      return await this.request<VoiceCompileResponse>(
        `/control/v1/channels/vapi/compile/${agentId}`,
        { method: 'POST' }
      );
    },

    /**
     * Sync compiled FOCAL rules to the live Vapi assistant.
     * Preserves assistant ID — no delete+recreate.
     */
    sync: async (agentId: string) => {
      return await this.request<{ agent_id: string; agent_name: string; assistant_id: string; status: string }>(
        `/control/v1/channels/vapi/sync/${agentId}`,
        { method: 'POST' }
      );
    },

    /**
     * Get full live Vapi assistant + phone config for the inspector.
     * Returns raw Vapi API objects.
     */
    inspect: async (agentId: string) => {
      return await this.request<VapiInspectResponse>(
        `/control/v1/channels/vapi/inspect/${agentId}`
      );
    },

    /**
     * List recent calls from Vapi for an agent's assistant.
     */
    listCalls: async (agentId: string, limit = 25) => {
      return await this.request<VapiCallsResponse>(
        `/control/v1/channels/vapi/calls/${agentId}?limit=${limit}`
      );
    },

    /**
     * Update specific voice/model settings on the Vapi assistant.
     * Only provided non-null fields are updated.
     */
    updateVoiceSettings: async (agentId: string, settings: VoiceSettingsUpdate) => {
      return await this.request<{ updated: boolean; assistant: Record<string, unknown> }>(
        `/control/v1/channels/vapi/voice-settings/${agentId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(settings),
        }
      );
    },
  };

  smsChannels = {
    /**
     * Create an SMS channel for an agent.
     * Reuses the agent's existing phone number (from voice).
     */
    create: async (agentId: string) => {
      return await this.request<SMSChannel>(
        '/control/v1/channels/sms',
        {
          method: 'POST',
          body: JSON.stringify({ agent_id: agentId }),
        }
      );
    },

    /**
     * List all SMS channels for the tenant.
     */
    list: async (agentId?: string) => {
      const searchParams = new URLSearchParams();
      if (agentId) searchParams.set('agent_id', agentId);
      const query = searchParams.toString();
      return await this.request<SMSChannelListResponse>(
        `/control/v1/channels/sms${query ? `?${query}` : ''}`
      );
    },

    /**
     * Delete an SMS channel.
     */
    delete: async (channelId: string) => {
      return await this.request<void>(
        `/control/v1/channels/sms/${channelId}`,
        {
          method: 'DELETE',
        }
      );
    },
  };

  // Runtime API - Sandbox Chat endpoints (for testing)
  sandboxChat = {
    /**
     * Send a message to an agent in sandbox mode
     * @param params.debug - Include full FOCAL pipeline debug info in response
     */
    sendMessage: async (params: {
      agent_id: string;
      message: string;
      session_id?: string;
      debug?: boolean;
    }) => {
      const queryParams = params.debug ? '?debug=true' : '';
      return await this.request<{
        session_id: string;
        agent_id: string;
        message: string;
        response: string;
        turn_number: number;
        processing_time_ms: number;
        matched_rules: string[];
        active_scenario: string | null;
        active_step: string | null;
        debug: DebugInfo | null;
      }>(`/v1/chat${queryParams}`, {
        method: 'POST',
        body: JSON.stringify({
          agent_id: params.agent_id,
          message: params.message,
          session_id: params.session_id,
        }),
      }, NO_TIMEOUT);
    },

    /**
     * Create a new sandbox session
     */
    createSession: async (agentId: string) => {
      return await this.request<{
        session_id: string;
        agent_id: string;
        channel: string;
        status: string;
        turn_count: number;
        created_at: string;
      }>('/v1/chat/sessions', {
        method: 'POST',
        body: JSON.stringify({ agent_id: agentId }),
      });
    },

    /**
     * Get sandbox session details
     */
    getSession: async (sessionId: string) => {
      return await this.request<{
        session_id: string;
        agent_id: string;
        channel: string;
        status: string;
        turn_count: number;
        created_at: string;
      }>(`/v1/chat/sessions/${sessionId}`);
    },

    /**
     * Delete a sandbox session
     */
    deleteSession: async (sessionId: string) => {
      return await this.request<void>(`/v1/chat/sessions/${sessionId}`, {
        method: 'DELETE',
      });
    },

    /**
     * Reset a sandbox session (clear history but keep session ID)
     */
    resetSession: async (sessionId: string) => {
      return await this.request<{
        session_id: string;
        agent_id: string;
        channel: string;
        status: string;
        turn_count: number;
        created_at: string;
      }>(`/v1/chat/sessions/${sessionId}/reset`, {
        method: 'POST',
      });
    },
  };

  // Runtime API - Conversations endpoints
  conversations = {
    list: async (params: ConversationListQuery) => {
      const searchParams = new URLSearchParams();
      if (params.agent_id) searchParams.set('agent_id', params.agent_id);
      if (params.is_sandbox !== undefined && params.is_sandbox !== null) {
        searchParams.set('is_sandbox', String(params.is_sandbox));
      }
      if (params.channel) searchParams.set('channel', params.channel);
      if (params.status) searchParams.set('status', params.status);
      if (params.since) searchParams.set('since', params.since);
      if (params.until) searchParams.set('until', params.until);
      if (params.limit) searchParams.set('limit', String(params.limit));
      if (params.offset) searchParams.set('offset', String(params.offset));
      const query = searchParams.toString();
      return await this.request<ConversationListResponse>(
        `/v1/conversations?${query}`
      );
    },

    get: async (sessionId: string, params?: ConversationDetailQuery) => {
      const searchParams = new URLSearchParams();
      if (params?.include_messages !== undefined) {
        searchParams.set('include_messages', String(params.include_messages));
      }
      if (params?.message_limit) searchParams.set('message_limit', String(params.message_limit));
      if (params?.message_offset) searchParams.set('message_offset', String(params.message_offset));
      const query = searchParams.toString();
      return await this.request<ConversationDetailResponse>(
        `/v1/conversations/${sessionId}${query ? `?${query}` : ''}`
      );
    },

    getMessages: async (sessionId: string, params?: ConversationMessagesQuery) => {
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.offset) searchParams.set('offset', String(params.offset));
      if (params?.since_turn !== undefined && params.since_turn !== null) {
        searchParams.set('since_turn', String(params.since_turn));
      }
      if (params?.role) searchParams.set('role', params.role);
      const query = searchParams.toString();
      return await this.request<ConversationMessagesResponse>(
        `/v1/conversations/${sessionId}/messages${query ? `?${query}` : ''}`
      );
    },

    getTurns: async (sessionId: string, params?: ConversationTurnsQuery) => {
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.offset) searchParams.set('offset', String(params.offset));
      const query = searchParams.toString();
      return await this.request<ConversationTurnsResponse>(
        `/v1/conversations/${sessionId}/turns${query ? `?${query}` : ''}`
      );
    },

    getAnalytics: async (params: ConversationAnalyticsQuery) => {
      const searchParams = new URLSearchParams();
      searchParams.set('agent_id', params.agent_id);
      if (params.is_sandbox !== undefined && params.is_sandbox !== null) {
        searchParams.set('is_sandbox', String(params.is_sandbox));
      }
      searchParams.set('since', params.since);
      searchParams.set('until', params.until);
      if (params.group_by) searchParams.set('group_by', params.group_by);
      const query = searchParams.toString();
      return await this.request<ConversationAnalyticsResponse>(
        `/v1/conversations/analytics?${query}`
      );
    },

    getMessageDebug: async (sessionId: string, messageId: string) => {
      return await this.request<MessageDebugInfo>(
        `/v1/conversations/${sessionId}/messages/${messageId}/debug`
      );
    },

    sendMessage: async (sessionId: string, params: SendMessageRequest) => {
      return await this.request<SendMessageResponse>(
        `/v1/conversations/${sessionId}/messages`,
        {
          method: 'POST',
          body: JSON.stringify(params),
        }
      );
    },

    search: async (params: ConversationSearchRequest) => {
      return await this.request<ConversationSearchResponse>(
        '/v1/conversations/search',
        {
          method: 'POST',
          body: JSON.stringify(params),
        }
      );
    },

    export: async (sessionId: string, format: ConversationExportFormat = 'json') => {
      const response = await fetch(
        `${API_BASE_URL}/v1/conversations/${sessionId}/export?format=${format}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new ApiError(
          `Export failed: ${response.statusText}`,
          response.status
        );
      }

      // Return raw response for downloads
      if (format === 'json') {
        return await response.json();
      } else {
        return await response.text();
      }
    },
  };

  // Runtime API - Interlocutors endpoints (person-centric profiles)
  interlocutors = {
    /**
     * List every interlocutor who has had a session with this agent
     * (Contacts tab — session-driven population, mirrors Conversations).
     */
    listContacts: async (params: ContactListQuery) => {
      const searchParams = new URLSearchParams();
      searchParams.set('agent_id', params.agent_id);
      if (params.q) searchParams.set('q', params.q);
      if (params.channel) searchParams.set('channel', params.channel);
      if (params.include_test !== undefined) {
        searchParams.set('include_test', String(params.include_test));
      }
      if (params.cursor) searchParams.set('cursor', params.cursor);
      if (params.limit) searchParams.set('limit', String(params.limit));
      return await this.request<ContactListResponse>(
        `/v1/interlocutors?${searchParams.toString()}`
      );
    },

    /**
     * This contact's sessions, bookings and callback tickets with one agent
     * (Contacts detail drawer).
     */
    getAgentEngagement: async (
      interlocutorId: string,
      agentId: string,
      params?: { session_limit?: number }
    ) => {
      const searchParams = new URLSearchParams();
      searchParams.set('agent_id', agentId);
      if (params?.session_limit) {
        searchParams.set('session_limit', String(params.session_limit));
      }
      return await this.request<ContactEngagementResponse>(
        `/v1/interlocutors/${interlocutorId}/agent-engagement?${searchParams.toString()}`
      );
    },

    /**
     * Get full interlocutor profile with fields, channels, and presence.
     */
    getProfile: async (interlocutorId: string) => {
      return await this.request<InterlocutorProfile>(
        `/v1/interlocutors/${interlocutorId}`
      );
    },

    /**
     * Get cross-channel messages for an interlocutor.
     */
    getMessages: async (interlocutorId: string, params?: { channel?: string; limit?: number; offset?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.channel) searchParams.set('channel', params.channel);
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.offset) searchParams.set('offset', String(params.offset));
      const query = searchParams.toString();
      return await this.request<InterlocutorMessage[]>(
        `/v1/interlocutors/${interlocutorId}/messages${query ? `?${query}` : ''}`
      );
    },

    /**
     * Mark interlocutor profile as reviewed.
     */
    markReviewed: async (interlocutorId: string, data: { reviewed_by: string; message_id?: string; notes?: string }) => {
      return await this.request<{ status: string; interlocutor_id: string }>(
        `/v1/interlocutors/${interlocutorId}/review`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    /**
     * List notes for an interlocutor.
     */
    listNotes: async (interlocutorId: string, params?: { limit?: number; offset?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.offset) searchParams.set('offset', String(params.offset));
      const query = searchParams.toString();
      return await this.request<{ notes: InterlocutorNote[] }>(
        `/v1/interlocutors/${interlocutorId}/notes${query ? `?${query}` : ''}`
      );
    },

    /**
     * Add a note to an interlocutor.
     */
    addNote: async (interlocutorId: string, data: { text: string; created_by: string }) => {
      return await this.request<{ id: string; status: string }>(
        `/v1/interlocutors/${interlocutorId}/notes`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    /**
     * Delete an interlocutor note.
     */
    deleteNote: async (interlocutorId: string, noteId: string) => {
      return await this.request<{ status: string }>(
        `/v1/interlocutors/${interlocutorId}/notes/${noteId}`,
        { method: 'DELETE' }
      );
    },
  };

  // Runtime API - Tickets endpoints
  tickets = {
    /**
     * List tickets for an interlocutor.
     */
    listForInterlocutor: async (interlocutorId: string, params?: { status?: string; limit?: number; offset?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.set('status', params.status);
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.offset) searchParams.set('offset', String(params.offset));
      const query = searchParams.toString();
      return await this.request<TicketListResponse>(
        `/v1/tickets/interlocutor/${interlocutorId}${query ? `?${query}` : ''}`
      );
    },

    /**
     * Get a single ticket.
     */
    get: async (ticketId: string) => {
      return await this.request<Ticket>(
        `/v1/tickets/${ticketId}`
      );
    },

    /**
     * Create a ticket.
     */
    create: async (data: {
      agent_id: string;
      interlocutor_id: string;
      session_id?: string;
      subject?: string;
      description?: string;
      priority?: string;
      created_by: string;
    }) => {
      return await this.request<Ticket>(
        '/v1/tickets/',
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    /**
     * Update a ticket.
     */
    update: async (ticketId: string, data: {
      status?: string;
      priority?: string;
      assigned_to?: string;
      subject?: string;
      description?: string;
      updated_by?: string;
    }) => {
      return await this.request<Ticket>(
        `/v1/tickets/${ticketId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      );
    },

    /**
     * List ticket notes.
     */
    listNotes: async (ticketId: string) => {
      return await this.request<TicketNote[]>(
        `/v1/tickets/${ticketId}/notes`
      );
    },

    /**
     * List ticket timeline events.
     */
    listEvents: async (ticketId: string) => {
      return await this.request<TicketEvent[]>(
        `/v1/tickets/${ticketId}/events`
      );
    },
  };

  // Control API — unified commercial surface (Task 073)
  commercial = {
    getPipeline: async (params?: {
      agent_id?: string;
      stage?: string;
      temperature?: string;
      commercial_status?: string;
      limit?: number;
    }) => {
      const search = new URLSearchParams();
      if (params?.agent_id) search.set('agent_id', params.agent_id);
      if (params?.stage) search.set('stage', params.stage);
      if (params?.temperature) search.set('temperature', params.temperature);
      if (params?.commercial_status)
        search.set('commercial_status', params.commercial_status);
      if (params?.limit) search.set('limit', String(params.limit));
      const qs = search.toString();
      return await this.request<CommercialPipelineResponse>(
        `/control/v1/interlocutors/commercial/pipeline${qs ? `?${qs}` : ''}`,
      );
    },

    getContext: async (profileId: string, activitiesLimit = 25) => {
      return await this.request<CommercialContextResponse>(
        `/control/v1/interlocutors/${profileId}/commercial?activities_limit=${activitiesLimit}`,
      );
    },

    updateCommercial: async (
      profileId: string,
      body: CommercialProfileUpdatePayload,
    ) => {
      return await this.request<CommercialProfileResponse>(
        `/control/v1/interlocutors/${profileId}/commercial`,
        { method: 'PATCH', body: JSON.stringify(body) },
      );
    },

    listLeads: async (params?: {
      profile_id?: string;
      stage?: string;
      limit?: number;
      offset?: number;
    }) => {
      const search = new URLSearchParams();
      if (params?.profile_id) search.set('profile_id', params.profile_id);
      if (params?.stage) search.set('stage', params.stage);
      if (params?.limit) search.set('limit', String(params.limit));
      if (params?.offset) search.set('offset', String(params.offset));
      const qs = search.toString();
      return await this.request<LeadSummary[]>(
        `/control/v1/interlocutors/commercial/leads${qs ? `?${qs}` : ''}`,
      );
    },

    listDeals: async (params?: {
      profile_id?: string;
      stage?: string;
      limit?: number;
      offset?: number;
    }) => {
      const search = new URLSearchParams();
      if (params?.profile_id) search.set('profile_id', params.profile_id);
      if (params?.stage) search.set('stage', params.stage);
      if (params?.limit) search.set('limit', String(params.limit));
      if (params?.offset) search.set('offset', String(params.offset));
      const qs = search.toString();
      return await this.request<DealSummary[]>(
        `/control/v1/interlocutors/commercial/deals${qs ? `?${qs}` : ''}`,
      );
    },

    listActivities: async (params?: {
      profile_id?: string;
      lead_id?: string;
      session_id?: string;
      agent_id?: string;
      type?: string;
      limit?: number;
      offset?: number;
    }) => {
      const search = new URLSearchParams();
      if (params?.profile_id) search.set('profile_id', params.profile_id);
      if (params?.lead_id) search.set('lead_id', params.lead_id);
      if (params?.session_id) search.set('session_id', params.session_id);
      if (params?.agent_id) search.set('agent_id', params.agent_id);
      if (params?.type) search.set('type', params.type);
      if (params?.limit) search.set('limit', String(params.limit));
      if (params?.offset) search.set('offset', String(params.offset));
      const qs = search.toString();
      return await this.request<ActivitySummary[]>(
        `/control/v1/interlocutors/commercial/activities${qs ? `?${qs}` : ''}`,
      );
    },

    getAgentMetrics: async (agentId: string) => {
      return await this.request<CommercialDashboardMetrics>(
        `/control/v1/agents/${agentId}/commercial-metrics`,
      );
    },
  };

  // Control API — agent operational dashboard overview
  agentOverview = {
    get: async (agentId: string, params?: { days?: number; include_test?: boolean }) => {
      const search = new URLSearchParams();
      if (params?.days) search.set('days', String(params.days));
      if (params?.include_test) search.set('include_test', String(params.include_test));
      const qs = search.toString();
      return await this.request<AgentOverviewResponse>(
        `/control/v1/agents/${agentId}/overview${qs ? `?${qs}` : ''}`,
      );
    },
  };

  // Control API — scheduling surface (Task 095)
  scheduling = {
    listBookings: async (params?: {
      team_id?: string;
      team_member_id?: string;
      state?: BookingState;
      start_at_min?: string;
      start_at_max?: string;
      limit?: number;
    }): Promise<BookingListResponse> => {
      const qs = new URLSearchParams();
      if (params?.team_id) qs.set('team_id', params.team_id);
      if (params?.team_member_id) qs.set('team_member_id', params.team_member_id);
      if (params?.state) qs.set('state', params.state);
      if (params?.start_at_min) qs.set('start_at_min', params.start_at_min);
      if (params?.start_at_max) qs.set('start_at_max', params.start_at_max);
      if (params?.limit) qs.set('limit', String(params.limit));
      const q = qs.toString();
      return await this.request<BookingListResponse>(
        `/control/v1/scheduling/bookings${q ? `?${q}` : ''}`,
      );
    },

    confirmBooking: async (bookingId: string): Promise<BookingResponse> =>
      await this.request<BookingResponse>(
        `/control/v1/scheduling/bookings/${bookingId}/confirm`,
        { method: 'PATCH', body: JSON.stringify({}) },
      ),

    cancelBooking: async (bookingId: string, reason?: string): Promise<BookingResponse> =>
      await this.request<BookingResponse>(
        `/control/v1/scheduling/bookings/${bookingId}/cancel`,
        { method: 'PATCH', body: JSON.stringify(reason ? { reason } : {}) },
      ),

    listMeetingTypes: async (params?: {
      team_id?: string;
      active_only?: boolean;
    }): Promise<MeetingTypeListResponse> => {
      const qs = new URLSearchParams();
      if (params?.team_id) qs.set('team_id', params.team_id);
      if (params?.active_only !== undefined) qs.set('active_only', String(params.active_only));
      const q = qs.toString();
      return await this.request<MeetingTypeListResponse>(
        `/control/v1/scheduling/meeting-types${q ? `?${q}` : ''}`,
      );
    },

    createMeetingType: async (payload: MeetingTypeCreatePayload): Promise<MeetingTypeResponse> =>
      await this.request<MeetingTypeResponse>('/control/v1/scheduling/meeting-types', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    updateMeetingType: async (
      id: string,
      payload: MeetingTypeUpdatePayload,
    ): Promise<MeetingTypeResponse> =>
      await this.request<MeetingTypeResponse>(`/control/v1/scheduling/meeting-types/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),

    archiveMeetingType: async (id: string): Promise<void> =>
      await this.request<void>(`/control/v1/scheduling/meeting-types/${id}/archive`, {
        method: 'PATCH',
        body: JSON.stringify({}),
      }),

    listTeams: async (): Promise<TeamListResponse> =>
      await this.request<TeamListResponse>('/control/v1/scheduling/teams'),

    /**
     * Mettre à jour un intervenant — aujourd'hui son FUSEAU.
     *
     * Le serveur porte cette écriture depuis Task 095 (PATCH
     * /scheduling/team-members/{id}, validation IANA côté modèle) et le Meta
     * Agent l'expose ; le console ne savait que LIRE la liste. Le fuseau de
     * l'intervenant est ce dans quoi le tool-gateway rend chaque créneau
     * (``_member_time_zones``) et ce que FOCAL reprend ensuite pour parler au
     * client — un tenant hors d'Europe/Paris n'avait donc aucun moyen de se
     * corriger depuis l'interface.
     */
    updateTeamMember: async (
      memberId: string,
      payload: TeamMemberUpdatePayload,
    ): Promise<TeamMemberResponse> =>
      await this.request<TeamMemberResponse>(
        `/control/v1/scheduling/team-members/${memberId}`,
        { method: 'PATCH', body: JSON.stringify(payload) },
      ),

    listTeamMembers: async (params?: { team_id?: string }): Promise<TeamMemberListResponse> => {
      const q = params?.team_id ? `?team_id=${params.team_id}` : '';
      return await this.request<TeamMemberListResponse>(
        `/control/v1/scheduling/team-members${q}`,
      );
    },
  };
}

// Commercial types ─────────────────────────────────────────────────
export type CommercialStatus = 'prospect' | 'customer' | 'churned';
export type CommercialStage =
  | 'prospect'
  | 'qualified'
  | 'proposal'
  | 'won'
  | 'lost'
  | 'dormant';
export type Temperature =
  | 'browsing'
  | 'inquiring'
  | 'evaluating'
  | 'negotiating'
  | 'signing'
  | 'retained'
  | 'at_risk';
export type ScoreTier = 'cold' | 'warm' | 'hot';

export interface CommercialProfileResponse {
  id: string;
  tenant_id: string;
  commercial_status: CommercialStatus | null;
  commercial_stage: CommercialStage | null;
  temperature: Temperature | null;
  temperature_reason: string | null;
  temperature_updated_at: string | null;
  lead_score: number | null;
  score_tier: ScoreTier | null;
  score_reasoning: string | null;
  score_updated_at: string | null;
  primary_email: string | null;
  primary_phone: string | null;
  display_name: string | null;
  company: string | null;
  created_at: string;
  updated_at: string;
}

export interface LeadSummary {
  id: string;
  stage: string;
  score: number | null;
  score_tier: ScoreTier | null;
  agent_id: string | null;
  session_id: string | null;
  source: string | null;
  created_at: string;
  updated_at: string;
}

export interface DealSummary {
  id: string;
  title: string;
  amount: string | number | null;
  currency: string;
  stage: string;
  expected_close_date: string | null;
  lead_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActivitySummary {
  id: string;
  type: string;
  summary: string | null;
  payload: Record<string, unknown>;
  created_by: string | null;
  lead_id: string | null;
  session_id: string | null;
  created_at: string;
}

export interface CommercialContextResponse {
  profile: CommercialProfileResponse;
  leads: LeadSummary[];
  deals: DealSummary[];
  activities: ActivitySummary[];
}

export interface CommercialPipelineColumn {
  stage: string;
  count: number;
  profiles: CommercialProfileResponse[];
}

export interface CommercialPipelineResponse {
  columns: CommercialPipelineColumn[];
  totals: Record<string, number>;
}

export interface CommercialProfileUpdatePayload {
  commercial_status?: CommercialStatus | null;
  clear_commercial_status?: boolean;
  commercial_stage?: CommercialStage | null;
  stage_reason?: string | null;
  temperature?: Temperature | null;
  temperature_reason?: string | null;
  lead_score?: number | null;
  score_tier?: ScoreTier | null;
  score_reasoning?: string | null;
}

export interface CommercialDashboardMetrics {
  contacts_total: number;
  contacts_by_source: Record<string, number>;
  pipeline: Record<string, number>;
  pipeline_value_open: string | number;
  pipeline_currency: string;
  conversion_rate_qualified_to_won_30d: number | null;
  temperature_distribution: Record<string, number>;
  active_conversations_today: number;
  top_hot_leads: CommercialProfileResponse[];
  recent_activities: ActivitySummary[];
}

// Agent overview dashboard types (control-api GET /agents/{id}/overview) ───

export interface PeriodDelta {
  current: number;
  previous: number;
  delta_pct: number | null;
}

export interface ChannelCount {
  channel: string;
  count: number;
}

export interface DailyChannelPoint {
  date: string;
  channel: string;
  count: number;
}

export interface HourlyActivityPoint {
  hour: number;
  count: number;
}

export interface UpcomingMeeting {
  id: string;
  start_at: string;
  end_at: string;
  meeting_type_label: string;
  team_member_name: string | null;
  customer_name: string | null;
}

export interface PendingCallback {
  id: string;
  reason: string;
  priority: string;
  status: string;
  created_at: string;
  callback_phone: string | null;
  callback_requested_at: string | null;
  session_id: string | null;
}

export interface OutcomeCount {
  outcome: string;
  count: number;
}

export interface AgentOverviewResponse {
  agent_id: string;
  period_days: number;
  include_test: boolean;
  timezone: string;
  conversations: PeriodDelta;
  unique_contacts: PeriodDelta;
  avg_turns_per_conversation: number | null;
  conversations_by_day: DailyChannelPoint[];
  channel_distribution: ChannelCount[];
  hourly_activity: HourlyActivityPoint[];
  meetings_booked: PeriodDelta;
  meetings_cancellation_rate: number | null;
  upcoming_meetings: UpcomingMeeting[];
  pending_callbacks_count: number;
  oldest_pending_callbacks: PendingCallback[];
  avg_sentiment: number | null;
  outcome_distribution: OutcomeCount[];
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export for use in components
export default apiClient;

// Scheduling types ─────────────────────────────────────────────────
export type BookingState = 'pending_confirmation' | 'confirmed' | 'cancelled';

export interface BookingResponse {
  id: string;
  organization_id: string;
  meeting_type_id: string;
  team_member_id: string;
  team_id: string;
  interlocutor_id: string | null;
  contact_id: string | null;
  customer_info: Record<string, unknown>;
  start_at: string;
  end_at: string;
  state: BookingState;
  hold_expires_at: string | null;
  language_used: string | null;
  idempotency_key: string;
  created_via: string;
  created_by: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_at: string;
  updated_at: string;
  /** Qui la réunion concerne réellement, quand ce n'est pas la personne qui a
   *  pris rendez-vous (ex : « pour mon directeur, Marc Aubert »). Voir W-DX. */
  beneficiary_name: string | null;
  beneficiary_email: string | null;
}

export interface BookingListResponse {
  bookings: BookingResponse[];
  total: number;
}

export type ConfirmationMode = 'immediate' | 'requires_confirmation';

export interface MeetingTypeResponse {
  id: string;
  organization_id: string;
  team_id: string;
  slug: string;
  label: Record<string, string>;
  description: string | null;
  duration_minutes: number;
  buffer_minutes_before: number;
  buffer_minutes_after: number;
  confirmation_mode: ConfirmationMode;
  required_attrs: Record<string, unknown>;
  metadata: Record<string, unknown>;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MeetingTypeListResponse {
  meeting_types: MeetingTypeResponse[];
  total: number;
}

export interface MeetingTypeCreatePayload {
  team_id: string;
  slug: string;
  label: Record<string, string>;
  description?: string;
  duration_minutes: number;
  buffer_minutes_before?: number;
  buffer_minutes_after?: number;
  confirmation_mode?: ConfirmationMode;
  required_attrs?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface MeetingTypeUpdatePayload {
  label?: string;
  description?: string;
  duration_minutes?: number;
  buffer_minutes_before?: number;
  buffer_minutes_after?: number;
  confirmation_mode?: ConfirmationMode;
  active?: boolean;
  metadata?: Record<string, unknown>;
}

export interface TeamResponse {
  id: string;
  organization_id: string;
  slug: string;
  label: Record<string, string>;
  kind: string | null;
  description: string | null;
  active: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface TeamListResponse {
  teams: TeamResponse[];
  total: number;
}

export interface TeamMemberResponse {
  id: string;
  organization_id: string;
  full_name: string;
  kind: 'human' | 'ai_agent';
  agent_id: string | null;
  active: boolean;
  bookable: boolean;
  time_zone: string;
  languages: string[];
  specialties: string[];
  created_at: string;
}

/** Ce que le PATCH accepte. Tout est optionnel : on n'envoie que ce qu'on change. */
export interface TeamMemberUpdatePayload {
  full_name?: string;
  bookable?: boolean;
  /** Nom IANA — le serveur refuse tout ce que ``zoneinfo`` ne connaît pas. */
  time_zone?: string;
  languages?: string[];
  specialties?: string[];
  active?: boolean;
}

export interface TeamMemberListResponse {
  members: TeamMemberResponse[];
  total: number;
}
